import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Pagination,
  Card,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { formatInTimeZone } from 'date-fns-tz';
import ImageCarouselDialog from 'app/views/Argyle/sites/ObservationSection/ImageCarouselDialog';
import { deleteARImage } from 'app/redux/reducers/ARImageReducer';
import { useDispatch } from 'react-redux';

const IMAGES_PER_PAGE = 20;

const ARImageGalleryPage = ({ images, projectId }) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [manageMode, setManageMode] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  const theme = useTheme();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const cols = isSmallScreen ? 1 : 3;

  const totalPages = Math.ceil(images.length / IMAGES_PER_PAGE);
  const paginatedImages = images.slice(
    (page - 1) * IMAGES_PER_PAGE,
    page * IMAGES_PER_PAGE,
  );

  const groupedImages = [];
  let currentDate = null;
  let counter = 0;
  paginatedImages.forEach((image) => {
    const timeStamp =
      image.metadata?.timeStamp &&
      !isNaN(new Date(image.metadata.timeStamp).getTime())
        ? new Date(image.metadata.timeStamp)
        : null;
    const dateKey = timeStamp
      ? formatInTimeZone(
          timeStamp,
          Intl.DateTimeFormat().resolvedOptions().timeZone,
          'MM/dd/yyyy',
        )
      : 'N/A';

    if (dateKey !== currentDate) {
      groupedImages.push({
        date: dateKey,
        images: [{ image, localIndex: counter }],
      });
      currentDate = dateKey;
    } else {
      groupedImages[groupedImages.length - 1].images.push({
        image,
        localIndex: counter,
      });
    }
    counter++;
  });

  const handleCloseCarousel = () => {
    setCarouselOpen(false);
  };

  // When normal mode, clicking an image opens the carousel
  // When manage mode, clicking toggles selection
  const onImageClick = (imageId, index) => {
    if (!manageMode) {
      // Normal mode -> open carousel
      setCarouselStartIndex(index);
      setCarouselOpen(true);
    } else {
      // Manage mode -> toggle selection
      setSelectedImageIds((prev) =>
        prev.includes(imageId)
          ? prev.filter((id) => id !== imageId)
          : [...prev, imageId],
      );
    }
  };

  // Single-image download (existing logic)
  const handleDownload = (e, image) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = image.augmentedUrl;
    link.download = image.augmentedUrl.split('/').pop();
    link.click();
  };

  // Batch download of all selected images
  const handleBatchDownload = () => {
    selectedImageIds.forEach((imageId) => {
      const image = images.find((img) => img.id === imageId);
      if (image) {
        const link = document.createElement('a');
        link.href = image.augmentedUrl;
        link.download = image.augmentedUrl.split('/').pop();
        link.click();
      }
    });
  };

  const handleBatchDelete = () => {
    selectedImageIds.forEach((imageId) => {
      dispatch(deleteARImage(projectId, imageId));
    });
    setSelectedImageIds([]);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    setSelectedImageIds([]);
  };

  const formattedDateTime = (timestamp) => {
    const UTC = timestamp + 'Z';
    return formatInTimeZone(
      new Date(UTC),
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      'MM/dd/yyyy HH:mm:ss',
    );
  };

  return (
    <Box p={3}>
      {/* Header Card */}
      <Card style={{ padding: '20px', marginBottom: '20px' }}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <PhotoLibraryIcon fontSize="large" />
          <Typography variant="h2" style={{ marginLeft: '8px' }}>
            AR Photo Gallery
          </Typography>
        </Box>
      </Card>

      {/* Manage Mode Controls */}
      <Card style={{ padding: '20px', marginBottom: '20px' }}>
        {!manageMode ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setManageMode(true)}
          >
            Enter Manage Mode
          </Button>
        ) : (
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBatchDownload}
              disabled={selectedImageIds.length === 0}
            >
              Download Selected
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBatchDelete}
              disabled={selectedImageIds.length === 0}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setManageMode(false);
                setSelectedImageIds([]);
              }}
            >
              Cancel Manage Mode
            </Button>
          </Box>
        )}
      </Card>

      {/* Images grouped by date */}
      <Card style={{ padding: '20px', marginBottom: '20px' }}>
        {groupedImages.map((group, groupIndex) => (
          <Box key={groupIndex} mb={4}>
            <Typography variant="h6" gutterBottom>
              {group.date}
            </Typography>
            <ImageList cols={cols} gap={16}>
              {group.images.map(({ image, localIndex }) => {
                const imageId = image.id || `local-${localIndex}`;
                const isSelected = selectedImageIds.includes(imageId);

                return (
                  <ImageListItem
                    key={imageId}
                    sx={{ cursor: 'pointer', position: 'relative' }}
                    onClick={() =>
                      onImageClick(
                        imageId,
                        (page - 1) * IMAGES_PER_PAGE + localIndex,
                      )
                    }
                  >
                    <img
                      src={image.arThumbnailUrl}
                      alt={`Thumbnail for ${image.id}`}
                      loading="lazy"
                      style={{
                        width: '100%',
                        display: 'block',
                        objectFit: 'contain',
                      }}
                    />
                    {/* Selection overlay if in manage mode */}
                    {manageMode && isSelected && (
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        bgcolor="rgba(0,0,0,0.5)"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 48, color: 'white' }}
                        />
                      </Box>
                    )}

                    <ImageListItemBar
                      title={
                        image?.metadata?.timeStamp
                          ? formattedDateTime(image.metadata.timeStamp)
                          : 'Image'
                      }
                      actionIcon={
                        // Hide the single-download icon when in manage mode,
                        // or keep it if you prefer both behaviors at once
                        !manageMode && (
                          <IconButton
                            onClick={(e) => handleDownload(e, image)}
                            sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        )
                      }
                    />
                  </ImageListItem>
                );
              })}
            </ImageList>
          </Box>
        ))}

        {/* Pagination if multiple pages */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        {/* Fullscreen carousel when not in manage mode */}
        <ImageCarouselDialog
          open={carouselOpen}
          onClose={handleCloseCarousel}
          images={images}
          initialIndex={carouselStartIndex}
        />
      </Card>
    </Box>
  );
};

ARImageGalleryPage.propTypes = {
  images: PropTypes.array.isRequired,
  projectId: PropTypes.string.isRequired,
};

export default ARImageGalleryPage;
